import { useMemo, useCallback } from 'react';

/**
 * useTableViewProps Hook
 * 
 * Assembles all props needed for the TableView component.
 */
const useTableViewProps = ({
  tasks,
  statuses,
  localSprint,
  allSprints = [],
  isTemplate,
  calculatedProgress,
  setDetailSidebarTaskId,
  setAttachmentsModalTaskId,
  sortSettingsPerView,
  filtersPerView,
  showArchived,
  showActionableOnly,
  actions,
  handlers,
  modals,
  nested,
  toolbar,
  flowchartRef,
  handleTrashNestedSprint,
  onSelectSprint,
  onSprintIconChange,
  // Handlers from parent
  handleDeleteTaskWithNestedCheck,
  handleDeleteTasksWithNestedCheck,
  handleIconChangeWithNestedSync,
  // Column visibility
  tableColumnVisibility,
  // Swimlane settings
  tableSwimlane,
  tableSwimlaneContext,
  // View navigation (for "Open in view" dropdown)
  currentView,
  customViews,
  activeCustomViewId,
  onViewChange,
  onSelectCustomView,
  // Deliverables warning modal
  onSubmitDeliverable,
}) => {
  // Handle focus task from table (switches to flow view)
  const handleFocusTaskFromTable = useCallback((taskId) => {
    toolbar.setCurrentView('flow');
    setTimeout(() => flowchartRef.current?.focusTask(taskId), 100);
  }, [toolbar, flowchartRef]);

  // Handle go to task in a specific view (view is already changed by caller)
  const handleGoToTaskInView = useCallback((taskId, viewType) => {
    // Dispatch event to focus the task in the target view
    // Uses the same event as MissionSearch for consistency
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('focusMission', {
        detail: { missionId: taskId, view: viewType }
      }));
    }, 100);
  }, []);

  const handleCreateNestedCampaignFromTable = useCallback(
    (taskId) => nested.handleCreateNestedSprint(taskId, { view: 'table' }),
    [nested.handleCreateNestedSprint]
  );

  return useMemo(() => ({
    tasks,
    categories: actions.getAllCategories(),
    allTags: actions.getAllTags(),
    statuses,
    nestedSprints: nested.nestedSprints,
    localSprint,
    allSprints,
    campaignId: localSprint?.id || '',
    campaignName: isTemplate ? '' : localSprint?.name,
    campaignIcon: localSprint?.icon,
    campaignProgress: calculatedProgress,
    breadcrumbPath: nested.breadcrumbPath,
    onNavigateToParent: nested.handleGoToParent,
    onSelectSprint,
    onSprintIconChange,
    onStatusChange: modals.handleStatusChangeWithModal,
    onPriorityChange: actions.handlePriorityChange,
    onDifficultyChange: actions.handleDifficultyChange,
    onDependencyChange: actions.handleDependencyChange,
    onConditionsChange: actions.handleConditionsChange,
    onDeleteTask: handleDeleteTaskWithNestedCheck,
    onDeleteTasks: handleDeleteTasksWithNestedCheck,
    onPasteTasks: handlers.handlePasteTasks,
    onDuplicateTask: handlers.handleDuplicateTaskWithNested,
    onAddTask: (category) => actions.handleAddTask(null, category, 'task'),
    onAddDecision: (category) => actions.handleAddTask(null, category, 'decision'),
    onAddWait: (category) => actions.handleAddTask(null, category, 'wait'),
    onRenameTask: actions.handleRenameTask,
    onCategoryChange: actions.handleCategoryChange,
    onRenameCategory: actions.handleRenameCategory,
    onDeleteCategory: actions.handleDeleteCategory,
    // Tag actions (multi-select)
    onAddTag: actions.handleAddTag,
    onRemoveTag: actions.handleRemoveTag,
    onSetPrimaryTag: actions.handleSetPrimaryTag,
    onRenameTag: actions.handleRenameTag,
    onDeleteTag: actions.handleDeleteTag,
    // Same as flow/kanban; keep table view after opening the new nested campaign
    onAddSubtask: handleCreateNestedCampaignFromTable,
    onFocusTask: handleFocusTaskFromTable,
    onOpenNestedSprint: nested.handleOpenNestedSprint,
    onRemoveNestedSprint: handleTrashNestedSprint,
    onAddStatus: handlers.handleAddStatus,
    onDeleteStatus: handlers.handleDeleteStatus,
    onRenameStatus: handlers.handleRenameStatus,
    onSetStatusAutoSync: actions.handleSetStatusAutoSync,
    onRenameSprint: handlers.handleRenameSprint,
    onIconChange: handleIconChangeWithNestedSync,
    onArchiveTask: actions.handleArchiveTask,
    onUnarchiveTask: actions.handleUnarchiveTask,
    onDecisionAnswerChange: actions.handleDecisionAnswerChange,
    onDecisionOptionsChange: actions.handleDecisionOptionsChange,
    showArchived,
    showActionableOnly,
    sortSetting: sortSettingsPerView?.table || 'manual',
    filters: filtersPerView?.table || {},
    columnVisibility: tableColumnVisibility,
    swimlaneSetting: tableSwimlane,
    swimlaneContext: tableSwimlaneContext,
    onOpenDetails: setDetailSidebarTaskId,
    onOpenAttachments: setAttachmentsModalTaskId,
    // Custom fields
    customFields: localSprint?.customFields || [],
    onCustomFieldValueChange: actions.handleCustomFieldValueChange,
    // View navigation
    currentView,
    customViews,
    activeCustomViewId,
    onViewChange,
    onSelectCustomView,
    onGoToTaskInView: handleGoToTaskInView,
    // Deliverables warning modal
    onSubmitDeliverable,
  }), [
    tasks, statuses, actions, nested, localSprint, allSprints, isTemplate,
    calculatedProgress, modals, handlers, handleTrashNestedSprint,
    handleFocusTaskFromTable, handleCreateNestedCampaignFromTable, onSelectSprint, onSprintIconChange,
    handleIconChangeWithNestedSync, showArchived, showActionableOnly,
    handleDeleteTaskWithNestedCheck, handleDeleteTasksWithNestedCheck, sortSettingsPerView, filtersPerView,
    tableColumnVisibility,
    tableSwimlane, tableSwimlaneContext,
    currentView, customViews, activeCustomViewId, onViewChange, onSelectCustomView,
    handleGoToTaskInView, onSubmitDeliverable,
  ]);
};

export default useTableViewProps;
